import React, { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { useLessonStore } from '@/store/lessonStore'
import { apiClient } from '@/services/api'
import toast from 'react-hot-toast'
import { Clock, Plus, Minus, RefreshCw, SkipForward } from 'lucide-react'
import { audioService } from '@/services/audio'

export function LessonTimer() {
  const { currentLesson, currentActivity, setCurrentActivity, suppressActivityPoll } = useLessonStore()
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null)
  const [isRunning, setIsRunning] = useState(false)
  const [extendMinutes, setExtendMinutes] = useState(5)
  const [isRefreshing, setIsRefreshing] = useState(false)
  // Prevent duplicate auto-complete calls
  const autoCompletedRef = useRef<string | null>(null)
  // Track which audio thresholds have fired to avoid repeat plays
  const firedWarningRef = useRef(false)
  const firedUrgentRef = useRef(false)

  const autoCompleteActivity = (lessonId: string, activityId: string) => {
    if (autoCompletedRef.current === activityId) return
    autoCompletedRef.current = activityId
    audioService.play('time-up')
    apiClient.completeActivity(lessonId, activityId)
      .then(response => {
        if (response.success) {
          const store = useLessonStore.getState()
          store.setCurrentActivity(null)
          store.setResponses([])
          if (response.data) {
            store.updateActivityInLesson(response.data)
          }
          toast.success('Activity completed — time expired', { duration: 3000, position: 'top-center' })
        }
      })
      .catch(() => {})
  }

  // No per-second API polling — the timer counts down locally.
  // LessonMaster's poll (every 5s) syncs currentActivity from the backend,
  // which re-triggers the effect below to recalculate timeRemaining.

  useEffect(() => {
    // Reset auto-complete guard and audio thresholds when activity changes
    if (currentActivity?.id !== autoCompletedRef.current) {
      autoCompletedRef.current = null
    }
    firedWarningRef.current = false
    firedUrgentRef.current = false

    if (!currentActivity || !currentActivity.time_limit_seconds) {
      setTimeRemaining(null)
      setIsRunning(false)
      return
    }

    // For active activities without started_at, fetch fresh data immediately
    if (currentActivity.status === 'active' && !currentActivity.started_at && currentLesson?.id) {
      const fetchFreshData = async () => {
        try {
          const response = await apiClient.getLessonActivities(currentLesson.id)
          if (response.success && response.data) {
            const freshActivity = response.data.find(a => a.id === currentActivity.id)
            if (freshActivity && freshActivity.started_at) {
              setCurrentActivity(freshActivity)
            }
          }
        } catch {
          // fetch failed
        }
      }
      fetchFreshData()
      return
    }

    // Calculate time remaining
    const now = Date.now()
    const startTime = currentActivity.started_at ? new Date(currentActivity.started_at).getTime() : now
    const timeLimit = currentActivity.time_limit_seconds * 1000
    const elapsed = now - startTime
    const remaining = Math.max(0, timeLimit - elapsed)

    setTimeRemaining(Math.floor(remaining / 1000))
    setIsRunning(currentActivity.status === 'active' && remaining > 0)

    // Auto-complete if time has already expired but activity is still active
    if (remaining <= 0 && currentActivity.status === 'active' && currentLesson?.id) {
      autoCompleteActivity(currentLesson.id, currentActivity.id)
    }
  }, [currentActivity, currentActivity?.id, currentActivity?.started_at, currentActivity?.status, currentActivity?.time_limit_seconds, currentLesson?.id, setCurrentActivity])

  useEffect(() => {
    if (!isRunning || timeRemaining === null || timeRemaining <= 0) {
      return
    }

    const interval = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev === null || prev <= 0) {
          setIsRunning(false)
          return 0
        }
        const next = prev - 1

        // Audio threshold checks
        if (next <= 30 && next > 10 && !firedWarningRef.current) {
          firedWarningRef.current = true
          audioService.play('timer-warning')
        }
        if (next <= 10 && next > 0 && !firedUrgentRef.current) {
          firedUrgentRef.current = true
          audioService.play('timer-urgent')
        }

        if (next <= 0) {
          setIsRunning(false)
          if (currentLesson?.id && currentActivity?.id) {
            autoCompleteActivity(currentLesson.id, currentActivity.id)
          }
          return 0
        }
        return next
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [isRunning, timeRemaining, currentLesson?.id, currentActivity?.id])

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  const handleExtendTime = async () => {
    if (!currentLesson?.id || !currentActivity?.id) return

    try {
      const response = await apiClient.extendActivityTime(currentLesson.id, currentActivity.id, extendMinutes)
      if (response.success) {
        // Suppress polling so stale data doesn't clobber the extend
        suppressActivityPoll(5000)

        if (response.data) {
          // Update the store with the new time_limit_seconds from the backend
          setCurrentActivity(response.data)

          // Recalculate remaining from the updated activity so it's accurate
          const updatedActivity = response.data as any
          if (updatedActivity.started_at && updatedActivity.time_limit_seconds) {
            const now = Date.now()
            const startTime = new Date(updatedActivity.started_at).getTime()
            const elapsed = now - startTime
            const remaining = Math.max(0, updatedActivity.time_limit_seconds - Math.floor(elapsed / 1000))
            setTimeRemaining(remaining)
          } else {
            // Fallback: just add the extension locally
            setTimeRemaining(prev => (prev ?? 0) + (extendMinutes * 60))
          }
        } else {
          setTimeRemaining(prev => (prev ?? 0) + (extendMinutes * 60))
        }
        // Only resume countdown if activity is active (not paused)
        if (currentActivity?.status === 'active') {
          setIsRunning(true)
        }
        toast.success(`Timer extended by ${extendMinutes} minute${extendMinutes > 1 ? 's' : ''}`)
      }
    } catch {
      toast.error('Failed to extend timer. Please try again.')
    }
  }

  const handleEndActivity = async () => {
    if (!currentLesson?.id || !currentActivity?.id) return

    try {
      const response = await apiClient.skipActivity(currentLesson.id, currentActivity.id)
      if (response.success) {
        const store = useLessonStore.getState()
        store.setCurrentActivity(null)
        store.setResponses([])
        if (response.data) {
          store.updateActivityInLesson(response.data)
        }
      }
    } catch {
      toast.error('Failed to end activity. Please try again.')
    }
  }

  const handleRefreshTimer = async () => {
    if (!currentLesson?.id) return

    try {
      setIsRefreshing(true)

      // Fetch fresh lesson data to get updated activity info
      const response = await apiClient.getLessonActivities(currentLesson.id)
      if (response.success && response.data) {
        const activeActivity = response.data.find(a => a.status === 'active')

        if (activeActivity) {
          setCurrentActivity(activeActivity)
          toast.success('Timer refreshed')
        } else {
          setCurrentActivity(null)
        }
      }
    } catch {
      toast.error('Failed to refresh timer')
    } finally {
      setIsRefreshing(false)
    }
  }

  const getTimerColor = () => {
    if (timeRemaining === null) return 'text-theme-text-muted'
    if (timeRemaining <= 30) return 'text-red-400'
    if (timeRemaining <= 60) return 'text-yellow-400'
    return 'text-green-400'
  }

  const getProgressPercentage = () => {
    if (!currentActivity?.time_limit_seconds || timeRemaining === null) return 0
    return (timeRemaining / currentActivity.time_limit_seconds) * 100
  }

  return (
    <div className="bg-surface-deep rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-[var(--color-primary)]" />
          <h3 className="ds-section-title">Activity Timer</h3>
        </div>
        <button
          onClick={handleRefreshTimer}
          disabled={isRefreshing}
          className="p-2 text-theme-text-muted hover:text-[var(--color-primary)] transition-colors disabled:opacity-50"
          title="Refresh timer state"
        >
          <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {currentActivity && currentActivity.time_limit_seconds ? (
        <div className="space-y-4">
          {/* Timer display */}
          <div className="text-center">
            <motion.div
              className={`text-4xl font-mono font-bold ${getTimerColor()}`}
              animate={{
                scale: timeRemaining !== null && timeRemaining <= 10 && isRunning ? [1, 1.05, 1] : 1
              }}
              transition={{
                duration: 0.5,
                repeat: timeRemaining !== null && timeRemaining <= 10 && isRunning ? Infinity : 0
              }}
            >
              {timeRemaining !== null ? formatTime(timeRemaining) : '--:--'}
            </motion.div>
            <div className="text-sm text-theme-text-muted mt-1">
              {(currentActivity.activity_data as any)?.question || (currentActivity.activity_data as any)?.title || currentActivity.title || 'Current Activity'}
            </div>
          </div>

          {/* Progress bar */}
          <div className="relative h-2 bg-surface-hover rounded-full overflow-hidden">
            <motion.div
              className={`absolute left-0 top-0 h-full rounded-full ${
                timeRemaining !== null && timeRemaining <= 30 ? 'bg-red-500' :
                timeRemaining !== null && timeRemaining <= 60 ? 'bg-yellow-500' :
                'bg-green-500'
              }`}
              initial={{ width: 0 }}
              animate={{ width: `${getProgressPercentage()}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>

          {/* Timer controls */}
          <div className="flex gap-2">
            <button
              onClick={handleEndActivity}
              className="ds-btn ds-btn-danger ds-btn-sm"
            >
              <SkipForward className="w-4 h-4" />
              End
            </button>
          </div>

          {/* Extend timer controls */}
          <div className="border-t border-theme-border pt-4">
            <div className="text-sm text-theme-text-muted mb-2">Extend Timer</div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setExtendMinutes(Math.max(1, extendMinutes - 1))}
                className="p-1 bg-surface-hover text-theme-text rounded hover:bg-surface-hover transition-colors"
              >
                <Minus className="w-4 h-4" />
              </button>

              <span className="px-3 py-1 bg-surface-hover rounded text-center min-w-[4rem]">
                {extendMinutes}m
              </span>

              <button
                onClick={() => setExtendMinutes(extendMinutes + 1)}
                className="p-1 bg-surface-hover text-theme-text rounded hover:bg-surface-hover transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>

              <button
                onClick={handleExtendTime}
                className="ds-btn ds-btn-primary ds-btn-sm"
              >
                Extend
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center text-theme-text-muted py-8">
          <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No active timer</p>
          <p className="text-sm mt-1">Timer will appear when an activity starts</p>
          {currentActivity && !currentActivity.time_limit_seconds && (
            <>
              <p className="text-xs mt-2 text-yellow-400">
                Current activity has no time limit set
              </p>
              <button
                onClick={handleEndActivity}
                className="ds-btn ds-btn-danger mt-3 mx-auto"
              >
                <SkipForward className="w-4 h-4" />
                End Activity
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}